import { memo } from "react"
import { makeStyles } from "tss-react/mui"
import { Box, ButtonBase, Tooltip } from "@mui/material"
import type { IconProp } from "@fortawesome/fontawesome-svg-core"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCalendarCheck, faChartBar, faMagnifyingGlassLocation } from "@fortawesome/pro-solid-svg-icons"

import { LocalizedEnum, LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"
import { useIsMobile } from "../../../../../hooks/useIsMobile"

const useStyles = makeStyles()((theme) => ({
    container: {
        display: "flex",
        border: `1px solid ${theme.palette.tailwind.slate[300]}`,
        borderRadius: ".75rem",
        overflow: "hidden",
        flex: "none",
        "& > * + *": {
            borderLeft: `1px solid ${theme.palette.tailwind.slate[300]}`,
        },
        [theme.breakpoints.down("sm")]: {
            "& > *": {
                flex: 1,
                justifyContent: "center",
            },
        },
    },
    icon: {
        fontSize: "1rem",
    },
    viewButton: {
        color: theme.palette.tailwind.slate[700],
        padding: ".75rem",
        background: theme.palette.common.white,
        display: "flex",
        alignItems: "center",
        gap: ".5rem",
        whiteSpace: "nowrap",
        fontSize: ".875rem",
        "&:hover": {
            background: theme.palette.grey[100],
        },
    },
    viewButtonSelected: {
        background: theme.palette.tailwind.slate[800],
        color: theme.palette.common.white,
        borderColor: theme.palette.tailwind.slate[800],
        "&:hover": {
            background: theme.palette.tailwind.slate[800],
        },
    },
    viewButtonUpcoming: {
        opacity: 0.5,
        cursor: "default",
        "&:hover": {
            background: theme.palette.common.white,
        },
    },
}))

export enum ViewType {
    TIMELINE = "TIMELINE",
    AVAILABLE_TIME = "AVAILABLE_TIME",
    ROOM_FINDER = "ROOM_FINDER",
}

const VIEWS: Record<ViewType, { icon: IconProp }> = {
    [ViewType.TIMELINE]: { icon: faChartBar },
    [ViewType.AVAILABLE_TIME]: { icon: faCalendarCheck },
    [ViewType.ROOM_FINDER]: { icon: faMagnifyingGlassLocation },
}

type BookingViewMenuProps = Readonly<{
    selectedView: ViewType
    onChangeView: (view: ViewType) => void
}>

export const BookingViewMenu = memo(function BookingViewMenu({ selectedView, onChangeView }: BookingViewMenuProps) {
    const { classes, cx } = useStyles()
    const isMobile = useIsMobile()

    return (
        <Box className={classes.container}>
            {Object.values(ViewType).map((view) => {
                const isSelected = view === selectedView

                if (view === ViewType.ROOM_FINDER) {
                    // a disabled teaser is not worth a third of a phone's width
                    if (isMobile) return null

                    return (
                        <LocalizedStrict
                            key={view}
                            id="create-order-view-room-finder-upcoming-tooltip"
                            attrs={{ title: true }}
                        >
                            <Tooltip title="Upcoming">
                                <ButtonBase className={cx(classes.viewButton, classes.viewButtonUpcoming)}>
                                    <FontAwesomeIcon icon={VIEWS[view].icon} className={classes.icon} />
                                    <LocalizedEnum base="create-order-view" enumValue={view}>
                                        <span>{view}</span>
                                    </LocalizedEnum>
                                </ButtonBase>
                            </Tooltip>
                        </LocalizedStrict>
                    )
                }

                return (
                    <ButtonBase
                        key={view}
                        className={cx(classes.viewButton, isSelected && classes.viewButtonSelected)}
                        onClick={!isSelected ? () => onChangeView(view) : undefined}
                    >
                        <FontAwesomeIcon icon={VIEWS[view].icon} />
                        <LocalizedEnum base="create-order-view" enumValue={view}>
                            <span>{view}</span>
                        </LocalizedEnum>
                    </ButtonBase>
                )
            })}
        </Box>
    )
})
